import { useEffect, useState, useCallback } from 'react';
import { getCorrectedDimensions } from './useCorrectedDimensions';

/**
 * Custom hook to handle responsive behavior in landscape mode
 * Determines if content fits in viewport and whether scrolling is needed
 * 
 * IMPORTANT: Uses corrected dimensions to handle WKWebView lying about
 * viewport size on initial render in landscape mode.
 */
export const useResponsiveLandscape = () => {
  const [contentFits, setContentFits] = useState(true);
  
  // Use corrected dimensions to determine initial state
  // This handles WKWebView lying about dimensions
  const [isLandscapeMobile, setIsLandscapeMobile] = useState(() => {
    const { isLandscape, width } = getCorrectedDimensions();
    // Mobile is when the CORRECTED width is <= 767px in portrait,
    // or when in landscape, check if we're on a mobile device
    const isMobile = Math.min(width, window.innerHeight) <= 767;
    return isLandscape && isMobile;
  });

  const checkLayout = useCallback(() => {
    // Use corrected dimensions to detect true orientation
    const { isLandscape, width, height } = getCorrectedDimensions();
    // Mobile check: the smaller dimension (portrait width) should be <= 767px
    const isMobile = Math.min(width, height) <= 767;
    const isLandscapeMobileMode = isLandscape && isMobile;
    
    setIsLandscapeMobile(isLandscapeMobileMode);

    if (!isLandscapeMobileMode) {
      setContentFits(true);
      return;
    }

    // Get the actual viewport height (accounting for browser bars)
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    
    // Get the document height (content height)
    const documentHeight = document.documentElement.scrollHeight;
    
    // Content fits if document height is less than or equal to viewport height
    const fits = documentHeight <= viewportHeight + 10; // 10px tolerance
    
    setContentFits(fits);
  }, []);

  useEffect(() => {
    // Initial check
    checkLayout();

    // Listen for resize events (including when browser bars appear/disappear)
    window.addEventListener('resize', checkLayout);
    
    // Listen for orientation changes
    window.addEventListener('orientationchange', checkLayout);
    
    // Listen for visualViewport changes (for mobile browser bar changes)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', checkLayout);
    }

    // Clean up
    return () => {
      window.removeEventListener('resize', checkLayout);
      window.removeEventListener('orientationchange', checkLayout);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', checkLayout);
      }
    };
  }, [checkLayout]);

  return {
    contentFits,
    isLandscapeMobile,
    needsScrolling: !contentFits && isLandscapeMobile,
  };
};

